// src/lib/schema/components/FAQSchema.tsx

import React from 'react';
import { generateFAQSchema, generateProductFAQSchema, generateShippingFAQSchema } from '../base/faq';
import { schemaToString } from '@/lib/schema';

/**
 * Interface for FAQ item
 */
interface FAQItem {
  question: string;
  answer: string;
}

/**
 * Props for the FAQSchema component
 */
interface FAQSchemaProps {
  faqs: FAQItem[];
}

/**
 * Component that renders the JSON-LD schema for FAQ pages
 */
export function FAQSchema({ faqs }: FAQSchemaProps): React.ReactElement | null {
  // Return null if required props are missing
  if (!faqs || !Array.isArray(faqs) || faqs.length === 0) return null;

  // Generate the FAQ schema
  const faqSchema = generateFAQSchema(faqs);

  // Return null if schema generation failed
  if (!faqSchema) return null;

  // Convert schema to JSON string
  const jsonLdString = schemaToString(faqSchema);

  // Render script tag with schema
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}

/**
 * Props for the ProductFAQSchema component
 */
interface ProductFAQSchemaProps {
  faqs: FAQItem[];
  product: {
    title: string;
    [key: string]: any;
  };
}

/**
 * Component that renders the JSON-LD schema for product-specific FAQs
 */
export function ProductFAQSchema({ faqs, product }: ProductFAQSchemaProps): React.ReactElement | null {
  // Return null if required props are missing
  if (!faqs || !Array.isArray(faqs) || faqs.length === 0) return null;
  if (!product || !product.title) return null;

  // Generate the product FAQ schema
  const productFaqSchema = generateProductFAQSchema(faqs, product);

  // Return null if schema generation failed
  if (!productFaqSchema) return null;

  // Convert schema to JSON string
  const jsonLdString = schemaToString(productFaqSchema);

  // Render script tag with schema
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}

/**
 * Interface for shipping information
 */
interface ShippingInfo {
  deliveryTime?: {
    min: number;
    max: number;
  };
  countries?: string[];
  cost?: string | number;
  returnPolicy?: string;
  internationalShipping?: boolean;
  additionalFaqs?: FAQItem[];
  [key: string]: any;
}

/**
 * Props for the ShippingFAQSchema component
 */
interface ShippingFAQSchemaProps {
  shippingInfo: ShippingInfo;
}

/**
 * Component that renders the JSON-LD schema for shipping/delivery FAQs
 */
export function ShippingFAQSchema({ shippingInfo }: ShippingFAQSchemaProps): React.ReactElement | null {
  // Return null if required props are missing
  if (!shippingInfo) return null;

  // Generate the shipping FAQ schema
  const shippingFaqSchema = generateShippingFAQSchema(shippingInfo);

  // Return null if schema generation failed
  if (!shippingFaqSchema) return null;

  // Convert schema to JSON string
  const jsonLdString = schemaToString(shippingFaqSchema);

  // Render script tag with schema
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdString }}
    />
  );
}